import { boolean, object, string } from 'yup';

import { CustomDemographics, CustomDemographicsPayload } from '../../../types';

export const supplementalSchema = object({
    preferred_contact_method: string().trim().notRequired(),
    preferred_contact_time: string().trim().notRequired(),
    caregiver_first_name: string().trim().notRequired(),
    caregiver_last_name: string().trim().notRequired(),
    caregiver_phone: string().trim().notRequired(),
    caregiver_email: string().trim().email('Please enter a valid email address.').notRequired(),
    caregiver_relationship: string().trim().notRequired(),
    device_pairing_status: string().trim().notRequired(),
    notes: string().trim().notRequired(),
    hero_ai_opt_in: boolean().notRequired()
});

export type SupplementalFormValues = {
    preferred_contact_method: string;
    preferred_contact_time: string;
    caregiver_first_name: string;
    caregiver_last_name: string;
    caregiver_phone: string;
    caregiver_email: string;
    caregiver_relationship: string;
    device_pairing_status: string;
    notes: string;
    hero_ai_opt_in: boolean;
};

// Documented vocabularies. The backend takes free strings, so these are a
// convenience for care managers rather than a constraint. '' clears the field.
export const CONTACT_METHODS = ['', 'phone', 'email', 'text'];
export const CONTACT_TIMES = ['', 'morning', 'afternoon', 'evening'];
export const PAIRING_STATUSES = ['', 'paired', 'unpaired', 'pending'];

export const toSupplementalFormValues = (
    demographics: CustomDemographics | null | undefined
): SupplementalFormValues => ({
    preferred_contact_method: demographics?.preferred_contact_method || '',
    preferred_contact_time: demographics?.preferred_contact_time || '',
    caregiver_first_name: demographics?.caregiver_first_name || '',
    caregiver_last_name: demographics?.caregiver_last_name || '',
    caregiver_phone: demographics?.caregiver_phone || '',
    caregiver_email: demographics?.caregiver_email || '',
    caregiver_relationship: demographics?.caregiver_relationship || '',
    device_pairing_status: demographics?.device_pairing_status || '',
    notes: demographics?.notes || '',
    hero_ai_opt_in: !!demographics?.hero_ai_opt_in
});

/** Blank inputs must clear the stored value, so send `null` rather than `''`. */
export const toSupplementalPayload = (
    values: SupplementalFormValues
): CustomDemographicsPayload => ({
    preferred_contact_method: values.preferred_contact_method || null,
    preferred_contact_time: values.preferred_contact_time || null,
    caregiver_first_name: values.caregiver_first_name || null,
    caregiver_last_name: values.caregiver_last_name || null,
    caregiver_phone: values.caregiver_phone || null,
    caregiver_email: values.caregiver_email || null,
    caregiver_relationship: values.caregiver_relationship || null,
    device_pairing_status: values.device_pairing_status || null,
    notes: values.notes || null,
    hero_ai_opt_in: !!values.hero_ai_opt_in
});

/** Payload for just `fields`, so saving a subset leaves the member's other values untouched. */
export const toPartialSupplementalPayload = (
    values: SupplementalFormValues,
    fields: readonly (keyof SupplementalFormValues)[]
): CustomDemographicsPayload => {
    const payload = toSupplementalPayload(values);

    return Object.fromEntries(fields.map((field) => [field, payload[field]]));
};

const formatEnum = (value?: string | null) => {
    const spaced = value?.trim().replace(/_/g, ' ');

    return spaced ? `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}` : '';
};

/** Read-only summary rows for the fields that have a value, in display order. */
export const getSupplementalSummary = (
    demographics: CustomDemographics | null | undefined
): { label: string; value: string }[] => {
    const caregiverName = [demographics?.caregiver_first_name, demographics?.caregiver_last_name]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');

    return [
        {
            label: 'Preferred contact method',
            value: formatEnum(demographics?.preferred_contact_method)
        },
        {
            label: 'Preferred contact time',
            value: formatEnum(demographics?.preferred_contact_time)
        },
        { label: 'Caregiver', value: caregiverName },
        { label: 'Caregiver relationship', value: formatEnum(demographics?.caregiver_relationship) }
    ].filter((row) => !!row.value);
};

/** Whether any field in `defaults` differs in `values`; keys outside `defaults` are ignored. */
export const hasChangedFrom = (values: Record<string, unknown>, defaults: object) =>
    Object.entries(defaults).some(([key, value]) => (values[key] ?? '') !== (value ?? ''));
